import styles from './NavSidebar.module.scss';
import React, { useCallback, useEffect, useMemo } from 'react';

import { SidebarRouteLink } from '@app/models/router/';

import { Drawer as MuiDrawer, DrawerProps as MuiDrawerProps, List, Toolbar, SwipeableDrawer } from '@mui/material';

import { NavSidebarLink } from './nav-sidebar-link/NavSidebarLink';
import { useStore } from '@app/store/useStore.hook';
import { useLocation } from 'react-use';
import { IconButton } from '@app/components';
import { clsxm } from '@app/styles/clsxm';
import { HomeButton } from '@app/components/buttons/home-button/HomeButton';
import CloseIcon from '@mui/icons-material/Close';

type NavSidebarProps = {
  badgesData?: Record<string, number>;
  navItems: SidebarRouteLink[][];
} & MuiDrawerProps;

export const NavSidebar: React.FC<NavSidebarProps> = ({ badgesData, navItems, ...rest }) => {
  const location = useLocation();

  // useEffect(() => {
  //   return history.listen(() => {
  //     if (navMobileOpen) {
  //       toggleNavMenu(false)();
  //     }
  //   });
  // }, [history, navMobileOpen, toggleNavMenu]);

  const {
    currentBreakpointDevice: { isDesktop },
    navMobileOpen,
    setNavMobile,
  } = useStore(({ currentBreakpointDevice, navMobileOpen, setNavMobile }) => ({
    navMobileOpen,
    currentBreakpointDevice,
    setNavMobile,
  }));

  const toggleNavMenu = useCallback(
    (open: boolean) => () => {
      setNavMobile(open);
    },
    [setNavMobile]
  );

  useEffect(() => {
    return () => {
      if (navMobileOpen) {
        toggleNavMenu(false)();
      }
    };
  }, [location, navMobileOpen, toggleNavMenu]);

  const navContent = useMemo(() => {
    return (
      <>
        {isDesktop && <Toolbar />}
        <List disablePadding>
          <div className={styles.ItemsWrapper}>
            {navItems.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.GroupWrapper}>
                {group.map((menuItem) => {
                  const badge = menuItem.badgeProp && badgesData ? badgesData[menuItem.badgeProp] : undefined;
                  return (
                    <NavSidebarLink
                      name={menuItem.name}
                      key={menuItem.name}
                      to={menuItem.path}
                      icon={menuItem.icon}
                      nestedLinks={menuItem.nestedLinks}
                      badge={badge}
                      badgesData={badgesData}
                      tooltip={menuItem.tooltip}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </List>
        {!isDesktop && <Toolbar />}
      </>
    );
  }, [badgesData, navItems, isDesktop]);

  if (!isDesktop) {
    return (
      <SwipeableDrawer
        classes={{ paper: clsxm(styles.Paper, 'w-[300px]'), root: styles.SwipeableRoot }}
        open={navMobileOpen}
        onOpen={toggleNavMenu(true)}
        onClose={toggleNavMenu(false)}
        ModalProps={{
          BackdropProps: { className: styles.MobileBackdrop },
        }}
        PaperProps={{
          className: 'min-w-[100%] sm:min-w-[300px]',
        }}
        elevation={0}
      >
        <div className="flex h-full flex-col justify-between">
          <Toolbar className="flex items-center gap-3 px-5">
            <IconButton
              size="medium"
              className={clsxm(styles['menu-icon-btn'], 'text-white')}
              variant="transparent"
              onClick={toggleNavMenu(false)}
              data-testid="sidebar-button-menu-close"
            >
              <CloseIcon />
            </IconButton>
            <div className="flex flex-1 justify-center">
              <HomeButton className="-ml-[50px] text-white" />
            </div>
          </Toolbar>
          {navContent}
        </div>
      </SwipeableDrawer>
    );
  }

  return (
    <MuiDrawer variant="permanent" {...rest} classes={{ paper: styles.Paper }}>
      {navContent}
    </MuiDrawer>
  );
};
